/**
 * Renderer-facing entry point: the shape a ViewerEngine's `setRibbonBuilder` expects (packages/state/src/engine.ts
 * RibbonBuilder): the component's residues (model residue indices) in, one mesh out. extractSegments splits them
 * into continuous segments, buildRibbon meshes each one, and the segment meshes are concatenated with index offsets
 * so one draw call covers the component. 1CRN at 8 steps
 * builds in well under a millisecond, 4HHB-size entries in a few. The app runs it in a Web Worker through
 * client.ts / worker.ts.
 */
import { buildRibbon } from './builder';
import { extractSegments } from './segments';
import type { RibbonBuildOptions, RibbonMeshBuffers, RibbonSourceModel } from './types';

const EMPTY: RibbonMeshBuffers = Object.freeze({
  position: new Float32Array(0), normal: new Float32Array(0), color: new Float32Array(0),
  residueIndex: new Uint32Array(0), index: new Uint32Array(0), vertexCount: 0, triangleCount: 0,
}) as RibbonMeshBuffers;

/** One mesh from several, vertex attributes appended and triangle indices shifted by the running vertex count. */
export function concatRibbonBuffers(parts: readonly RibbonMeshBuffers[]): RibbonMeshBuffers {
  const live = parts.filter((p) => p.vertexCount > 0);
  if (live.length === 0) return EMPTY;
  if (live.length === 1) return live[0]!;
  let vertexCount = 0, indexCount = 0;
  for (const p of live) { vertexCount += p.vertexCount; indexCount += p.index.length; }
  const position = new Float32Array(vertexCount * 3), normal = new Float32Array(vertexCount * 3), color = new Float32Array(vertexCount * 3);
  const residueIndex = new Uint32Array(vertexCount), index = new Uint32Array(indexCount);
  let v = 0, i = 0;
  for (const p of live) {
    position.set(p.position.subarray(0, p.vertexCount * 3), v * 3);
    normal.set(p.normal.subarray(0, p.vertexCount * 3), v * 3);
    color.set(p.color.subarray(0, p.vertexCount * 3), v * 3);
    residueIndex.set(p.residueIndex.subarray(0, p.vertexCount), v);
    for (let k = 0; k < p.index.length; k++) index[i + k] = p.index[k]! + v;
    v += p.vertexCount;
    i += p.index.length;
  }
  return { position, normal, color, residueIndex, index, vertexCount, triangleCount: indexCount / 3 };
}

/** All atoms of the given residues, sorted (extractSegments takes an atom set; residues outside it are absent). */
export function atomsOfResidues(model: RibbonSourceModel, residueIndices: Uint32Array): Uint32Array {
  let n = 0;
  for (let k = 0; k < residueIndices.length; k++) n += model.residues.atomCount[residueIndices[k]!] ?? 0;
  const out = new Uint32Array(n);
  let w = 0;
  for (let k = 0; k < residueIndices.length; k++) {
    const r = residueIndices[k]!;
    const start = model.residues.atomStart[r] ?? 0, count = model.residues.atomCount[r] ?? 0;
    for (let a = start; a < start + count; a++) out[w++] = a;
  }
  return out.sort();
}

/**
 * RibbonBuilder for the engine: `residueIndices` are the component's polymer residues, `colorOf` the colour of the
 * residue's trace atom under the component's coloring, options from the quality tier. Returns a single-element
 * array (the engine accepts a list) or an empty one when nothing can be drawn.
 */
export function buildRibbonMeshes(model: RibbonSourceModel, residueIndices: Uint32Array, colorOf: (residueIndex: number) => [number, number, number], options: RibbonBuildOptions): RibbonMeshBuffers[] {
  if (residueIndices.length === 0) return [];
  const segments = extractSegments(model, atomsOfResidues(model, residueIndices), colorOf);
  const mesh = concatRibbonBuffers(segments.map((s) => buildRibbon(s, options)));
  return mesh.vertexCount > 0 ? [mesh] : [];
}
